import type { SessionWorkspace } from '@ai-sales/shared'
import { createContext, use, type ReactNode } from 'react'
import { Navigate, Outlet, useParams } from 'react-router'
import { EmptyState } from '../components/data-table.js'
import { useCurrentUser } from '../auth/session-context.js'

/**
 * Текущий воркспейс.
 *
 * Он берётся из адреса (`/w/:workspaceId/...`), а не из хранилища браузера,
 * и это принципиально. Во-первых, ссылка на экран остаётся ссылкой: её можно
 * отправить коллеге, и он увидит то же самое. Во-вторых, две вкладки с
 * разными воркспейсами не мешают друг другу — с сохранённым «текущим»
 * воркспейсом переключение в одной вкладке молча меняло бы данные в другой,
 * и это ровно тот класс ошибок, когда действие выполняется не над тем
 * объектом, над которым человек его задумал.
 *
 * Список доступных воркспейсов приходит вместе с сессией, поэтому проверка
 * членства здесь не стоит отдельного запроса. Настоящая проверка — на
 * сервере: чужой идентификатор в адресе получит «не найдено» от API.
 */
const WorkspaceContext = createContext<SessionWorkspace | null>(null)

/**
 * Провайдер отдельно от layout'а: так экран можно отрисовать в тесте,
 * не поднимая маршрутизатор и сессию. Приём общий — компонент, который
 * нельзя проверить без половины приложения, обычно делает слишком много.
 */
export function WorkspaceProvider({
  workspace,
  children,
}: {
  workspace: SessionWorkspace
  children: ReactNode
}) {
  return <WorkspaceContext value={workspace}>{children}</WorkspaceContext>
}

export function WorkspaceLayout() {
  const user = useCurrentUser()
  const { workspaceId } = useParams<{ workspaceId: string }>()

  const workspace = user.workspaces.find((w) => w.id === workspaceId)

  if (user.workspaces.length === 0) {
    return (
      <div className="mx-auto max-w-lg p-8">
        <EmptyState
          title="У вас нет доступа ни к одному воркспейсу"
          description="Попросите владельца добавить вас в участники. Первый воркспейс создаётся командой bootstrap на сервере."
        />
      </div>
    )
  }

  if (!workspace) {
    /**
     * Адрес указывает на воркспейс, которого у человека нет. Это бывает не
     * только при подборе идентификатора: доступ могли отозвать, пока
     * страница была открыта. Поэтому не ошибка, а спокойный переход
     * к первому доступному.
     */
    const first = user.workspaces[0]

    return first ? <Navigate to={`/w/${first.id}`} replace /> : null
  }

  return (
    <WorkspaceProvider workspace={workspace}>
      <Outlet />
    </WorkspaceProvider>
  )
}

export function useCurrentWorkspace(): SessionWorkspace {
  const workspace = use(WorkspaceContext)

  if (!workspace) {
    throw new Error('useCurrentWorkspace вызван вне WorkspaceLayout')
  }

  return workspace
}

/** Перенаправление с корня на первый доступный воркспейс. */
export function WorkspaceRedirect({ children }: { children?: ReactNode }) {
  const user = useCurrentUser()
  const first = user.workspaces[0]

  if (!first) return <>{children}</>

  return <Navigate to={`/w/${first.id}`} replace />
}
